import { useTranslation } from 'react-i18next';
import { Cloud, RefreshCw } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui';
import { groupBySpanId, isSpanGroup } from './span-grouping';
import { CloudWatchEventRow } from './CloudWatchEventRow';
import { SpanPair } from './SpanPair';
import type { AdminTraceResponse } from '@/types/admin-trace';

export interface CloudWatchPanelProps {
  enabled: boolean;
  isLoading: boolean;
  error: unknown;
  trace: AdminTraceResponse | undefined;
  /** Erster Klick auf den Toggle — aktiviert die CW-Query (Opt-in, Trap §8.7). */
  onLoad: () => void;
  /**
   * Manuelles Neuladen via refetch() — der einzige Weg, die teure Query
   * erneut auszufuehren (staleTime Infinity, Spec §5.4 / Plan Phase 3).
   */
  onReload: () => void;
}

function hasWarning(trace: AdminTraceResponse | undefined, code: string): boolean {
  return (trace?.warnings ?? []).some((w) => w.code === code);
}

/**
 * CloudWatch-Bereich der Trace-Ansicht (Spec 143 §5.3/§5.4).
 * Opt-in per Toggle (Phase-1-Trace reicht fuer die meisten Faelle, CW ist
 * Deep-Dive mit 2-15s Ladezeit). Waehrend des Ladens bleibt die
 * Basis-Ansicht stehen — nur dieser Bereich zeigt einen Skeleton.
 */
export function CloudWatchPanel({ enabled, isLoading, error, trace, onLoad, onReload }: CloudWatchPanelProps) {
  const { t } = useTranslation('transactions');

  let body: React.ReactNode;

  if (!enabled) {
    body = (
      <div className="py-4 text-center">
        <Button variant="outline" onClick={onLoad} data-testid="cw-load-button">
          <Cloud className="mr-2 h-4 w-4" aria-hidden />
          {t('trace.cloudwatch.loadButton')}
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">{t('trace.cloudwatch.loadHint')}</p>
      </div>
    );
  } else if (isLoading) {
    body = (
      <div className="space-y-3" data-testid="cw-loading">
        <p className="text-sm text-muted-foreground">{t('trace.cloudwatch.loadingHint')}</p>
        {/* Pulse kommuniziert die 2-15s-Wartezeit; motion-reduce respektiert OS-Einstellung */}
        <Skeleton className="h-14 motion-reduce:animate-none" />
        <Skeleton className="h-14 motion-reduce:animate-none" />
        <Skeleton className="h-14 motion-reduce:animate-none" />
      </div>
    );
  } else if (error) {
    body = (
      <div className="py-4 text-center" data-testid="cw-error">
        <p className="text-sm text-destructive">{t('trace.cloudwatch.errorText')}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={onReload}>
          <RefreshCw className="mr-2 h-3 w-3" aria-hidden />
          {t('trace.cloudwatch.retry')}
        </Button>
      </div>
    );
  } else {
    const events = trace?.cloudWatchEvents ?? [];
    const cwMeta = trace?.meta?.cloudWatchMeta;

    if (events.length === 0) {
      // Kontextspezifische Empty-States (Spec §5.4)
      let emptyText = t('trace.cloudwatch.emptyNeutral');
      let showReload = false;
      if (hasWarning(trace, 'CLOUDWATCH_LOGS_EXPIRED')) {
        emptyText = t('trace.cloudwatch.emptyExpired');
      } else if (hasWarning(trace, 'CLOUDWATCH_LOGS_DELAYED')) {
        emptyText = t('trace.cloudwatch.emptyDelayed');
        showReload = true;
      }
      body = (
        <div className="py-4 text-center" data-testid="cw-empty">
          <p className="text-sm text-muted-foreground">{emptyText}</p>
          {showReload && (
            <Button variant="outline" size="sm" className="mt-2" onClick={onReload} data-testid="cw-reload-button">
              <RefreshCw className="mr-2 h-3 w-3" aria-hidden />
              {t('trace.cloudwatch.reload')}
            </Button>
          )}
        </div>
      );
    } else {
      const grouped = groupBySpanId(events);
      const incomplete = cwMeta?.truncated === true || hasWarning(trace, 'CLOUDWATCH_PARTIAL_RESULTS');

      body = (
        <div className="space-y-3" data-testid="cw-events">
          {incomplete && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="cw-incomplete-hint">
              {t('trace.cloudwatch.incompleteHint')}
            </p>
          )}
          {grouped.map((item, idx) =>
            isSpanGroup(item) ? (
              <SpanPair key={`span-${item.spanId}-${idx}`} group={item} />
            ) : (
              <CloudWatchEventRow key={item.eventId ?? idx} event={item} />
            ),
          )}
          {cwMeta && (
            <div className="border-t pt-2 text-xs text-muted-foreground" data-testid="cw-meta-footer">
              <p>
                {t('trace.cloudwatch.metaEvents')}: {cwMeta.eventCount ?? events.length}
                {cwMeta.queryDurationMs != null && <> · {t('trace.cloudwatch.metaDuration')}: {cwMeta.queryDurationMs} ms</>}
                {cwMeta.timeWindow?.from && cwMeta.timeWindow?.to && (
                  <> · {t('trace.cloudwatch.metaWindow')}: {cwMeta.timeWindow.from} – {cwMeta.timeWindow.to}</>
                )}
                {cwMeta.truncated != null && (
                  <> · {t('trace.cloudwatch.metaTruncated')}: {cwMeta.truncated ? t('trace.cloudwatch.yes') : t('trace.cloudwatch.no')}</>
                )}
              </p>
              {cwMeta.queriedLogGroups && cwMeta.queriedLogGroups.length > 0 && (
                <p className="mt-0.5 break-all font-mono">
                  {t('trace.cloudwatch.metaQueried')}: {cwMeta.queriedLogGroups.join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }
  }

  return (
    <Card data-testid="cw-panel">
      <CardHeader>
        <CardTitle className="text-base">{t('trace.cloudwatch.title')}</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
